import React, { useMemo, useRef, useState } from 'react';
import { transferirMatricula } from '../../../../services/matriculasService';
import { showToast } from '../../../../utils/showToast';
import { nombreCompleto } from '../adapters/matriculasAdapter';

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;
const IVA = 0.15;
// OJO: nunca usar new Date().toISOString().slice(0,10) para "hoy" — toISOString()
// convierte a UTC, y en Ecuador (UTC-5) desde las 19:00 ya marca el dia siguiente.
const hoy = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

function calcularTotal(precioBase, descPromoPct, descIndPct) {
  const descPromo = precioBase * (descPromoPct / 100);
  const descInd   = precioBase * (descIndPct   / 100);
  const subtotal  = Math.max(precioBase - descPromo - descInd, 0);
  return Number((subtotal + subtotal * IVA).toFixed(2));
}

export default function TransferirMatriculaModal({ matricula, promocionesOptions = [], onClose, onTransferida }) {
  const [promoSeleccionada, setPromoSeleccionada]   = useState(null);
  const [busqueda, setBusqueda]                     = useState('');
  const [menuAbierto, setMenuAbierto]               = useState(false);
  const [observacion, setObservacion]               = useState('');
  const [registrarAnterior, setRegistrarAnterior]   = useState(false);
  const [centroFormacionAnt, setCentroFormacionAnt] = useState('');
  const [fechaAprobacionAnt, setFechaAprobacionAnt] = useState('');
  const [aplicarDescuento, setAplicarDescuento]     = useState(false);
  const [pctDescuento, setPctDescuento]             = useState('');
  const [guardando, setGuardando]                   = useState(false);
  const inputRef = useRef(null);

  const cursoActualId = matricula?.cursoId ?? null;
  const pagado = (matricula?.totalAPagar ?? 0) - (matricula?.saldo ?? 0);

  const opcionesFiltradas = useMemo(() => {
    const term = busqueda.toLowerCase().trim();
    return promocionesOptions.filter(p =>
      p.value !== matricula?.promocionId &&
      (!term || p.label.toLowerCase().includes(term))
    );
  }, [promocionesOptions, busqueda, matricula?.promocionId]);

  const cursoCambia = promoSeleccionada && cursoActualId && promoSeleccionada.cursoId !== cursoActualId;

  // precio real sin descuento individual
  const totalReal = promoSeleccionada
    ? calcularTotal(promoSeleccionada.precioBase, promoSeleccionada.descuentoPorcentaje, 0)
    : null;

  // precio con descuento autorizado (si aplica)
  const pctNum = aplicarDescuento ? Math.min(Math.max(Number(pctDescuento) || 0, 0), 100) : 0;
  const totalConDescuento = promoSeleccionada
    ? calcularTotal(promoSeleccionada.precioBase, promoSeleccionada.descuentoPorcentaje, pctNum)
    : null;

  const totalFinal = aplicarDescuento ? totalConDescuento : totalReal;
  const nuevoSaldo = totalFinal !== null ? Math.max(totalFinal - pagado, 0) : null;

  const seleccionar = (opcion) => {
    setPromoSeleccionada(opcion);
    setBusqueda('');
    setMenuAbierto(false);
    setAplicarDescuento(false);
    setPctDescuento('');
  };

  const limpiar = () => {
    setPromoSeleccionada(null);
    setBusqueda('');
    setMenuAbierto(false);
    setAplicarDescuento(false);
    setPctDescuento('');
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleConfirmar = async () => {
    if (!promoSeleccionada) {
      showToast({ type: 'warning', message: 'Selecciona la promoción destino' });
      return;
    }
    if (!observacion.trim()) {
      showToast({ type: 'warning', message: 'Ingresa el motivo del traslado' });
      return;
    }
    setGuardando(true);
    try {
      const res = await transferirMatricula(matricula.id, {
        nueva_promocion_id:           promoSeleccionada.value,
        observacion:                  observacion.trim(),
        registrar_formacion_anterior: cursoCambia ? registrarAnterior : false,
        centro_formacion_anterior:    (cursoCambia && registrarAnterior && centroFormacionAnt.trim()) ? centroFormacionAnt.trim() : null,
        fecha_aprobacion_anterior:    (cursoCambia && registrarAnterior && fechaAprobacionAnt) ? fechaAprobacionAnt : null,
        descuento_individual_nuevo:   aplicarDescuento ? pctNum : 0,
      });
      showToast({ type: 'success', message: `Matrícula transferida. Nuevo código: ${res.nuevoCodigo}` });
      onTransferida(res.matricula);
      onClose();
    } catch (err) {
      showToast({ type: 'danger', message: err?.message || 'Error al transferir la matrícula' });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="modal fade show d-block management-modal" tabIndex="-1" role="dialog" aria-modal="true"
      style={{ backgroundColor: 'rgba(15,23,42,0.45)' }}>
      <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
        <div className="modal-content">

          <div className="modal-header border-0">
            <h5 className="modal-title">
              <i className="bi bi-arrow-left-right me-2 text-warning" />
              Transferir matrícula
            </h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={guardando} />
          </div>

          <div className="modal-body">

            {/* Info actual */}
            <div className="alert alert-secondary py-2 mb-3">
              <div className="fw-semibold small">{nombreCompleto(matricula)}</div>
              <div className="text-muted" style={{ fontSize: 12 }}>
                Código actual: <strong>{matricula?.codigoMatricula || '—'}</strong>
                &nbsp;·&nbsp; Promoción: <strong>{matricula?.promocion || `#${matricula?.promocionId}`}</strong>
                &nbsp;·&nbsp; Saldo: <strong>{fmt(matricula?.saldo)}</strong>
              </div>
            </div>

            {/* Buscador */}
            <div className="mb-3 position-relative">
              <label className="form-label fw-semibold small">
                Promoción destino <span className="text-danger">*</span>
              </label>
              <div className="input-group">
                <span className="input-group-text">
                  <i className="bi bi-search" style={{ fontSize: 12 }} />
                </span>
                <input
                  ref={inputRef}
                  type="text"
                  className="form-control"
                  placeholder={promoSeleccionada ? promoSeleccionada.label : 'Buscar promoción por nombre o curso…'}
                  value={promoSeleccionada ? promoSeleccionada.label : busqueda}
                  disabled={!!promoSeleccionada || guardando}
                  onChange={e => { setBusqueda(e.target.value); setMenuAbierto(true); }}
                  onFocus={() => { if (!promoSeleccionada) setMenuAbierto(true); }}
                  onBlur={() => setTimeout(() => setMenuAbierto(false), 150)}
                  autoComplete="off"
                />
                {promoSeleccionada && (
                  <button type="button" className="btn btn-outline-secondary" onClick={limpiar} disabled={guardando} title="Limpiar selección">
                    <i className="bi bi-x-lg" />
                  </button>
                )}
              </div>
              {menuAbierto && !promoSeleccionada && (
                <div className="list-group shadow position-absolute w-100"
                  style={{ zIndex: 1060, top: '100%', left: 0, maxHeight: 220, overflowY: 'auto' }}>
                  {opcionesFiltradas.length === 0
                    ? <div className="list-group-item text-muted small py-2">Sin resultados</div>
                    : opcionesFiltradas.map(p => (
                        <button key={p.value} type="button"
                          className="list-group-item list-group-item-action py-2 px-3 small"
                          onMouseDown={e => e.preventDefault()}
                          onClick={() => seleccionar(p)}>
                          {p.label}
                        </button>
                      ))}
                </div>
              )}
            </div>

            {/* Resumen financiero */}
            {promoSeleccionada && (
              <div className="card border-0 bg-light mb-3">
                <div className="card-body py-2 px-3">
                  <div className="row g-2 small">
                    <div className="col-6">
                      <div className="text-muted">Total actual</div>
                      <div className="fw-semibold">{fmt(matricula?.totalAPagar)}</div>
                    </div>
                    <div className="col-6">
                      <div className="text-muted">
                        Nuevo total
                        {aplicarDescuento && pctNum > 0 && (
                          <span className="text-warning ms-1">({pctNum}% desc.)</span>
                        )}
                      </div>
                      <div className={`fw-semibold ${totalFinal !== matricula?.totalAPagar ? 'text-warning' : ''}`}>
                        {fmt(totalFinal)}
                        {aplicarDescuento && pctNum > 0 && totalReal !== totalConDescuento && (
                          <span className="text-muted fw-normal ms-1" style={{ fontSize: 11 }}>
                            (real: {fmt(totalReal)})
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="text-muted">Ya pagado</div>
                      <div className="fw-semibold text-success">{fmt(pagado)}</div>
                    </div>
                    <div className="col-6">
                      <div className="text-muted">Nuevo saldo</div>
                      <div className={`fw-semibold ${nuevoSaldo > 0 ? 'text-danger' : 'text-success'}`}>
                        {fmt(nuevoSaldo)}
                      </div>
                    </div>
                    {cursoCambia && (
                      <div className="col-12">
                        <span className="badge bg-info text-dark">
                          <i className="bi bi-arrow-up-circle me-1" />
                          Cambio de curso — se asignará nuevo código en {promoSeleccionada.label.split('|')[0]?.trim()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Descuento autorizado */}
            {promoSeleccionada && (
              <div className="mb-3">
                <div className="form-check">
                  <input type="checkbox" className="form-check-input" id="chk-descuento"
                    checked={aplicarDescuento}
                    onChange={e => { setAplicarDescuento(e.target.checked); if (!e.target.checked) { setPctDescuento(''); } }}
                  />
                  <label className="form-check-label small" htmlFor="chk-descuento">
                    Aplicar descuento individual en el curso destino
                  </label>
                </div>
                {aplicarDescuento && (
                  <div className="row g-2 mt-1 ps-4">
                    <div className="col-sm-4">
                      <label className="form-label small mb-1">Porcentaje <span className="text-danger">*</span></label>
                      <div className="input-group input-group-sm">
                        <input
                          type="number"
                          className="form-control"
                          min="0" max="100" step="0.5"
                          placeholder="0"
                          value={pctDescuento}
                          onChange={e => setPctDescuento(e.target.value)}
                        />
                        <span className="input-group-text">%</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Formación anterior */}
            {cursoCambia && (
              <div className="mb-3">
                <div className="form-check">
                  <input type="checkbox" className="form-check-input" id="chk-formacion-anterior"
                    checked={registrarAnterior}
                    onChange={e => setRegistrarAnterior(e.target.checked)}
                  />
                  <label className="form-check-label small" htmlFor="chk-formacion-anterior">
                    Registrar la formación del curso anterior como <strong>acreditada</strong>
                    <span className="text-muted"> (el estudiante ya lo aprobó en otro centro)</span>
                  </label>
                </div>
                {registrarAnterior && (
                  <div className="row g-2 mt-1 ps-4">
                    <div className="col-sm-7">
                      <label className="form-label small mb-1">Centro donde lo aprobó <span className="text-muted">(opcional)</span></label>
                      <input type="text" className="form-control form-control-sm"
                        placeholder="Ej: SECAP Quito"
                        value={centroFormacionAnt}
                        onChange={e => setCentroFormacionAnt(e.target.value)}
                        maxLength={150}
                      />
                    </div>
                    <div className="col-sm-5">
                      <label className="form-label small mb-1">Fecha de aprobación <span className="text-muted">(opcional)</span></label>
                      <input type="date" className="form-control form-control-sm"
                        value={fechaAprobacionAnt}
                        max={hoy()}
                        onChange={e => setFechaAprobacionAnt(e.target.value)}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Motivo */}
            <div className="mb-1">
              <label className="form-label fw-semibold small">
                Motivo del traslado <span className="text-danger">*</span>
              </label>
              <textarea className="form-control form-control-sm" rows={2}
                placeholder="Ej: El estudiante no puede asistir a las fechas de la promoción 029…"
                value={observacion}
                onChange={e => setObservacion(e.target.value)}
                maxLength={300}
              />
            </div>

            <div className="alert alert-warning py-2 mt-3 small mb-0">
              <i className="bi bi-exclamation-triangle me-1" />
              El contrato actual quedará <strong>anulado</strong>. Deberás generar uno nuevo tras el traslado.
            </div>

          </div>

          <div className="modal-footer border-0">
            <button type="button" className="btn btn-secondary btn-sm" onClick={onClose} disabled={guardando}>
              Cancelar
            </button>
            <button type="button" className="btn btn-warning btn-sm" onClick={handleConfirmar}
              disabled={guardando || !promoSeleccionada || !observacion.trim()}>
              {guardando
                ? <><span className="spinner-border spinner-border-sm me-1" />Transfiriendo…</>
                : <><i className="bi bi-arrow-left-right me-1" />Confirmar traslado</>}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}
